/**
 * Mapping config `joint_offsets`: optional per-bone rotation corrections (degrees).
 * Keys are skeleton bone refs (short refs or full path refs).
 * Stored under MappingResult.ioExtras[joint_offsets].
 */

export const JOINT_OFFSETS_IO_KEY = 'joint_offsets';

export function parseRotationTriplet(raw: unknown): [number, number, number] | null {
  if (!Array.isArray(raw) || raw.length < 3) return null;
  const a = Number(raw[0]);
  const b = Number(raw[1]);
  const c = Number(raw[2]);
  if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(c)) return null;
  return [a, b, c];
}

export function getJointOffsetRotationForBoneRef(
  ioExtras: Record<string, unknown>,
  boneRef: string,
): [number, number, number] | null {
  const root = ioExtras[JOINT_OFFSETS_IO_KEY];
  if (!root || typeof root !== 'object' || Array.isArray(root)) return null;
  const entry = (root as Record<string, unknown>)[boneRef];
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  return parseRotationTriplet((entry as Record<string, unknown>).rotation);
}

export function getJointOffsetRotationEntries(
  ioExtras: Record<string, unknown>,
): Array<[string, [number, number, number]]> {
  const root = ioExtras[JOINT_OFFSETS_IO_KEY];
  if (!root || typeof root !== 'object' || Array.isArray(root)) return [];

  const entries: Array<[string, [number, number, number]]> = [];
  for (const [boneRef, entry] of Object.entries(root as Record<string, unknown>)) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const rotation = parseRotationTriplet((entry as Record<string, unknown>).rotation);
    if (!rotation) continue;
    entries.push([boneRef, rotation]);
  }
  return entries;
}

// Backward-compatible alias for legacy call sites.
export const getJointOffsetRotationForBoneName = getJointOffsetRotationForBoneRef;
